<script>
	const _ = require('underscore');
	const moment = require('moment');
	const Q = require('q');

	const utilities = require('utilities');
	const classUtilities = require('utilities/classUtilities');
	const studentGroupUtilities = require('utilities/studentGroupUtilities');

	const productService = require('services/productService');
	const assignmentService = require('services/assignmentService');

	const { authenticateWithGoogleClassroom, isKnownGoogleClass } = require('utilities/googleClassroomUtilities');

	const analyticsService = require('services/analyticsService');
	const analyticsUtilities = require('utilities/analyticsUtilities');
	const fireEvent = analyticsUtilities.fireEvent;
	const SCOPE_SEQUENCE_CREATED = analyticsUtilities.events.SCOPE_SEQUENCE_CREATED;
	const { updatePPAMilestone } = require('utilities/journeyUtilities');

	const EMPTY_DATA = {
		step: 0,
		eventData: {},

		startDate: moment().toDate().getTime(),
		endDate: moment().add(7, 'days').toDate().getTime(),
		useSameDates: 'yes',
		startDates: null,
		endDates: null,
		bok: true,
		boost: false,
		challenge: false,
		decodables: false,
		audio: false,
		studentChoice: '',
		classCode: null,
		students: [],
		group: null,
		assignTo: 's',
		ell: false,
		gc: true,
		wordDetective: false,
		meaningMapper: false,
		vocabulary: false,
		questionSets: false,
		fullOrExpress: null
	};

	module.exports = {
		props: {
			defaults: {
				type: Object
			},
			productIds: {
				type: Array
			},
			title: {
				type: String
			},
			subtitle: {
				type: String
			},
			categoryText: {
				type: String
			},
			hasVocabActivity: {
				type: Boolean
			},
			hasQuestionSets: {
				type: Boolean
			},
			hasExpressQuestionSets: {
				type: Boolean
			},
			flexibleDates: {
				type: Boolean
			},
			textSet: {
				type: Object
			}
		},
		components: {
			modal: require('./modal.vue').default,
			'article-image': require('components/article-image.vue').default,
			'validated-form': require('components/validated-form.vue').default,
			'validated-input': require('components/validated-input.vue').default,
			'info-icon': require('components/svg/info-icon.vue').default,
			'date-picker': require('components/date-picker.vue').default,
			'apple-icon': require('components/svg/apple-icon.vue').default,
			'lightning-icon': require('components/svg/lightning-icon.vue').default,
			message: require('components/message.vue').default
		},
		modelData: ['activeClasses', 'roster', 'studentGroups', 'localGCAccessToken', 'gcClassList', 'gcAddon'],
		data() {
			let today = moment().toDate().getTime();
			let raw = {
				products: [],
				selectedProducts: [],
				selectedProductsSet: new Set(),
				loadingRoster: false,
				loadingClasses: false,
				loadingProducts: false,
				loadingGroups: false,
				loadingAssignment: false,
				studentChoices: [
					{
						text: 'Students read the same articles',
						value: 'o'
					},
					{
						text: 'Students choose between two articles',
						value: 't'
					},
					{
						text: 'Students can choose any article in the set',
						value: ''
					}
				],
				today,
				ellTip:
					'For Article-A-Day only, this provides audio and additional features for students. Teachers decide which students have access to these extra supports. <a href="/teacher-guide/differentiation-tools.html#ell">Learn how to turn it on and what it provides.</a>',
				studentChoiceTip:
					'This controls how many articles a student has access to when they open their Article-A-Day set. If you select "Students read the same articles" only one article in the set will be available at a time. The next article will become available when they’ve completed the first one.',
				dueDateTooltip: 'Students will be able to continue working on assignments after the due date passes.',
				vocabTooltip: 'Assign activities designed for students to more deeply explore the vocabulary highlighted in the passage.'
			};
			Object.keys(EMPTY_DATA).forEach(k => {
				raw[k] = EMPTY_DATA[k];
			});

			return raw;
		},
		computed: {
			boostArticlesPresent() {
				return (this.products || []).filter(p => p.supplemental_texts && p.supplemental_texts.support).length > 0;
			},
			challengeArticlesPresent() {
				return (this.products || []).filter(p => p.supplemental_texts && p.supplemental_texts.challenge).length > 0;
			},
			wordDetectivePresent() {
				return (this.products || []).filter(p => p.word_detective).length > 0;
			},
			meaningMapperPresent() {
				return (this.products || []).filter(p => p.meaning_mapper).length > 0;
			},
			decodablesPresent() {
				const eventData = this.eventData;
				const isTopicalSS = eventData && eventData.id === 1101;
				return isTopicalSS && this.products.some(p => p.dteGroups);
			},
			loading() {
				return this.loadingProducts || this.loadingRoster || this.loadingClasses || this.loadingGroups || this.loadingAssignment;
			},
			hasAudio() {
				const products = this.products || [];

				return products.some(p => p.hasAudio());
			},
			productType() {
				const textSet = this.textSet;
				if (textSet) {
					return 'TS';
				}

				const products = this.products;
				if (products && products.length) {
					return products[0].productType;
				}

				return null;
			},
			currentClass() {
				let roster = this.roster;
				let cc = this.classCode;
				let classes = this.classes;
				let current = null;
				if (roster && roster[cc] && roster[cc].students) {
					let classMeta = {};
					classes.some(c => {
						if (c.classCode === cc) {
							classMeta = c;
							return true;
						}
						return false;
					});
					current = _.assign({}, classMeta, roster[cc]);
				}
				return current;
			},
			currentClassHasEll: function () {
				let allStudents = this.allStudents;
				if (allStudents && allStudents.length) {
					let enable = allStudents.some(s => s.ell && s.ell !== '');
					return enable;
				}
				return false;
			},
			classes() {
				let activeClasses = this.activeClasses;
				let cls = utilities.sortObjects(activeClasses, 'name');
				return cls;
			},
			allStudents() {
				let currentClass = this.currentClass;
				let allStudentsList = [];

				if (currentClass && currentClass.students) {
					allStudentsList = currentClass.students.map(s => {
						return _.assign({}, s, {
							name: s.firstName + (s.lastName ? ' ' + s.lastName : '')
						});
					});
				}
				return allStudentsList;
			},
			groups() {
				let studentGroups = this.studentGroups || [];
				let studentsById = (this.currentClass && this.currentClass.studentsById) || {};

				return studentGroups.reduce((acc, g) => {
					let suids = Object.keys(g.suids || {});
					let count = 0;
					suids.forEach(suid => {
						if (studentsById[suid]) {
							count++;
						}
					});
					if (count) {
						acc.push({
							text: g.n,
							disabled: count < 1,
							value: g.id
						});
					}
					return acc;
				}, []);
			},
			gcTokenMismatch() {
				const gcid = this.currentClass && this.currentClass.gcid;

				return gcid && this.gcClassList && !isKnownGoogleClass(gcid);
			},
			needsGoogleAuth() {
				const gcid = this.currentClass.gcid;
				const gcPublish = this.gc;
				/* No GCA check needed - inaccessible in GCA */

				return gcid && gcPublish && (!this.localGCAccessToken || this.gcTokenMismatch);
			},
			options() {
				let opts = [];
				const optionMap = new Map([
					['audio', 'Audio'],
					['bok', 'Book of Knowledge'],
					['boost', 'Boost Articles'],
					['challenge', 'Challenge Articles'],
					['decodables', 'Decodables'],
					['vocabulary', 'Vocabulary Activities'],
					['questionSets', `${this.fullOrExpress === 'f' ? 'Full' : 'Express'} Question Sets`]
				]);

				for (const [key, value] of optionMap) {
					if (this[key] === true) {
						opts.push(value);
					}
				}

				if (opts.length === 0) {
					opts = ['None'];
				}

				return opts.join(', ');
			},
			productListTitle() {
				const isTextSet = this.productType === 'TS';
				const productCount = (this.products || []).length;

				if (isTextSet) {
					return `Passages (${this.selectedProductsSet.size}/${productCount}):`;
				}

				return `Assignments (${productCount}):`;
			},
			lastStep() {
				if (this.flexibleDates) {
					return 4;
				}

				return 3;
			}
		},
		methods: {
			reset() {
				let defaults = this.defaults || {};
				let defaultKeys = Object.keys(defaults);
				Object.keys(EMPTY_DATA).forEach(k => {
					this[k] = EMPTY_DATA[k];
				});
				if (defaultKeys && defaultKeys.length) {
					defaultKeys.forEach(k => {
						this[k] = defaults[k];
					});

					if (this.products) {
						this.selectedProducts = this.products.map(product => product.id);
					} else {
						this.selectedProducts = [];
					}
				}

				this.loadClasses();
				this.startDate = moment().toDate().getTime();
				this.endDate = moment().add(7, 'days').toDate().getTime();
				this.resetFlexibleDates();
			},
			setProducts(list) {
				let products = [];
				let productPromise = Q();

				if (list && list.length) {
					this.loadingProducts = true;
					let productRequests = list.map(p => {
						// bug: WEB-2603
						// Need to ensure ReadWorksProduct object is base in _.assign, not plain object
						return productService.get(p.pid).then(product => products.push(_.assign(product, p)));
					});
					productPromise = Q.all(productRequests)
						.then(() => {
							this.products = products || [];
							this.selectedProducts = products.map(product => product.id);

							return;
						})
						.finally(() => (this.loadingProducts = false));
				}

				return productPromise;
			},
			getOffsetDateTime(offset, date) {
				let length = 1000 * 60 * 60 * 24 * 7; // 7 days offset
				let startDate = date;

				return moment(startDate + length * offset)
					.toDate()
					.getTime();
			},
			getFormattedDate(date, format = 'MM/DD') {
				return moment(date).format(format);
			},
			getOffsetDate(offset, date) {
				let preText = '';
				let startDateTime = this.getOffsetDateTime(offset, date);
				let startDate = this.getFormattedDate(startDateTime);

				if (startDate === this.getFormattedDate(this.today)) {
					preText = '(Today) ';
				}

				return preText + startDate;
			},
			nextStep() {
				this.step += 1;
			},
			previousStep() {
				this.step -= 1;
			},
			loadClasses() {
				let prms = classUtilities.loadActiveClasses();
				this.loadingClasses = true;
				prms
					.then(classes => {
						this.classCode = null;
						if (classes && classes.length) {
							let cls = classes[0];
							this.classCode = cls.classCode;
						} else {
							const textSet = this.textSet;
							utilities.openModal('#modal-create-class', {
								createAssignmentMessage: true,
								returnToProduct: textSet && textSet.pid
							});
						}
					})
					.finally(() => (this.loadingClasses = false));
				return prms;
			},
			loadRoster() {
				let cc = this.classCode;
				this.loadingRoster = true;
				return classUtilities.fetchRoster(cc).finally(() => (this.loadingRoster = false));
			},
			loadGroups() {
				let cc = this.classCode;
				let prms = Q();
				this.loadingGroups = true;
				if (cc && cc !== 'new') {
					prms = studentGroupUtilities.fetchGroups(cc).finally(() => (this.loadingGroups = false));
				}
				return prms;
			},
			toggleAllStudents(event) {
				let value = event && event.currentTarget ? event.currentTarget.checked : false;
				if (value && this.allStudents && this.allStudents.length) {
					this.students = this.allStudents.map(s => s.uid);
				} else {
					this.students = [];
				}
			},
			getSSAssignments(baseParams) {
				let startDate = this.startDate;
				let endDate = this.endDate;
				let studentChoice = this.studentChoice ? this.studentChoice : undefined;
				let template = Object.assign(
					{
						ss: true,
						assignArticle: true,
						showResults: true,
						assignAudio: this.audio,
						assignBookOfKnowledge: this.bok,
						classCode: this.classCode,
						ell: this.ell,
						productType: 'AAD',
						studentChoice
					},
					baseParams
				);

				let assignments = this.products.map((p, idx) => {
					const assignment = _.assign(
						{
							productId: p.id,
							ssp: p.ssp,
							date: this.getOffsetDateTime(idx, startDate),
							deadline: this.getOffsetDateTime(idx, endDate)
						},
						template
					);

					if (this.boost && p.supplemental_texts && p.supplemental_texts.support) {
						assignment.supportArticles = true;
					}
					if (this.challenge && p.supplemental_texts && p.supplemental_texts.challenge) {
						assignment.challengeArticles = true;
					}
					if (this.wordDetective && p.word_detective) {
						assignment.wordDetective = true;
					}
					if (this.meaningMapper && p.meaning_mapper) {
						assignment.meaningMapper = true;
					}
					if (this.decodables && p.dteGroups) {
						// update if flexibility given to assignable dte group
						assignment.decodables = p.dteGroups.map(group => group.id);
					}

					return assignment;
				});

				fireEvent(SCOPE_SEQUENCE_CREATED, this.eventData);
				if (this.eventData && this.eventData.id) {
					let id = this.eventData.id;
					let event = null;

					if (id === 1101) {
						event = 'assignAADSStopical';
					} else if (id === 1102) {
						event = 'assignAADSSvocab';
					}
					if (event) {
						analyticsService.activeCampaignEvents({ event });
					}
				}

				return assignments;
			},
			getTSAssignments(baseParams) {
				const startDates = this.startDates;
				const endDates = this.endDates;
				const assignVocabActivities = this.vocabulary;
				const assignQuestionSets = this.questionSets;
				const fullOrExpress = this.fullOrExpress;
				const selectedProductsSet = this.selectedProductsSet;
				const textSet = this.textSet;

				let template = Object.assign(
					{
						assignArticle: true,
						showResults: true,
						assignAudio: this.audio,
						classCode: this.classCode,
						productType: 'TS'
					},
					baseParams
				);

				const assignments = this.products
					.filter(p => selectedProductsSet.has(p.id))
					.map(p => {
						let questionSets;

						if (assignQuestionSets && p.questionSets) {
							questionSets = p.questionSetsOrdered.map(qs => ({
								fullOrExpress: qs.abridged_question_ids ? fullOrExpress : undefined,
								qsid: qs.qsid,
								selected: true
							}));
						}

						let vocabularyActivities;

						if (assignVocabActivities && p.vocab && p.vocab.activity) {
							vocabularyActivities = Object.keys(p.vocab.activity);
						}

						// submit deadline as 11:59PM on the day selected.
						const deadline = moment(endDates[p.id]).add(1, 'days').subtract(1, 'minutes').valueOf();

						const assignment = Object.assign(
							{
								productId: p.id,
								productType: p.productType,
								date: startDates[p.id],
								deadline,
								questionSets,
								vocabularyActivities,
								textSet
							},
							template
						);

						return assignment;
					});

				return assignments;
			},
			async createAssignments() {
				let baseParams = {};

				const localGCAccessToken = this.localGCAccessToken;
				const students = this.students;
				const currentClass = this.currentClass;
				const assignTo = this.assignTo;
				const selectedGroup = this.group;

				this.loadingAssignment = true;

				if (this.gc && currentClass.gcid && localGCAccessToken) {
					baseParams.token = localGCAccessToken;
					baseParams.gc = this.gc;
					baseParams.publishToGoogleClassroom = true;
				}

				if (assignTo === 's' && students.length !== currentClass.students.length) {
					// Specific Students Assignment
					baseParams.studentSelections = students.reduce((acc, s) => {
						acc.push({
							uid: s,
							selected: true
						});
						return acc;
					}, []);
				} else if (assignTo === 'g') {
					// Group Assignment
					baseParams.selectedGroup = selectedGroup;
				}

				const productType = this.productType;
				let assignments = [];

				if (productType === 'TS') {
					assignments = this.getTSAssignments(baseParams);
				} else if (productType === 'AAD') {
					assignments = this.getSSAssignments(baseParams);
				}

				let ppaPromises = [];
				const assignmentCreationPromises = assignments.map(async a => {
					const { productMetaData } = await assignmentService.createAssignment(a);
					ppaPromises.push(() => updatePPAMilestone(productMetaData));
				});

				if (productType === 'TS') {
					ppaPromises.push(() => updatePPAMilestone({ productType, ...this.textSet }));
				}

				try {
					await Promise.all(assignmentCreationPromises);
					this.nextStep();
				} catch (e) {
					if (e.code === 1115) {
						this.loadGroups();
						this.step = 1;
					}
				} finally {
					this.loadingAssignment = false;
				}

				for (const promise of ppaPromises) {
					await promise();
				}
			},
			authenticateWithGoogle() {
				authenticateWithGoogleClassroom();
			},
			closeModal() {
				utilities.closeModals();
			},
			setStartDates() {
				const startDate = this.startDate;
				let startDates = {};

				for (const { id } of this.products) {
					startDates[id] = startDate;
				}

				this.startDates = startDates;
			},
			setEndDates() {
				const endDate = this.endDate;
				let endDates = {};

				for (const { id } of this.products) {
					endDates[id] = endDate;
				}

				this.endDates = endDates;
			},
			resetFlexibleDates() {
				this.setStartDates();
				this.setEndDates();
			},
			onFlexibleStartDateSelect(date, pid) {
				this.endDates[pid] = moment(date).add(7, 'days').toDate().getTime();
			}
		},
		watch: {
			productIds(list) {
				if (list && list.length) {
					this.setProducts(list);
				}
			},
			startDate(date) {
				if (date) {
					this.endDate = moment(date).add(7, 'days').toDate().getTime();
					this.setStartDates();
				}
			},
			endDate(date) {
				if (date) {
					this.setEndDates();
				}
			},
			classCode(cc) {
				this.loadRoster();
				this.loadGroups();
			},
			currentClass(cls) {
				this.gc = true;
				this.ell = false;

				if (cls && cls.settings) {
					this.gc = cls.settings.publishToGoogleClassroom !== false;
				}
			},
			allStudents(v) {
				this.students = [];
				if (v && v.length) {
					this.students = v.map(s => s.uid);
				}
			},
			groups(v) {
				this.group = null;
				if (v && v.length) {
					this.group = v[0].value;
				}
			},
			useSameDates() {
				this.resetFlexibleDates();
			},
			selectedProducts(products) {
				this.selectedProductsSet = new Set(products);
			}
		},
		mounted() {
			this.$subscribe('modal-opened', (modalId, opts) => {
				if (modalId === 'assignment-wizard') {
					this.reset();
					if (opts && opts.eventData) {
						this.eventData = opts.eventData;
					}
				}
			});
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'mixins/media';

	#assignment-wizard {
		.wizard-nav {
			display: flex;

			.spacer {
				flex: 1;
			}
		}

		.input-wrapper {
			color: $c-slate-dark;
			margin-bottom: 0.5rem;
		}

		.checkbox-wrapper {
			display: flex;
			align-items: center;

			&.all-students {
				margin-left: 0.5rem;
				font-weight: bold;
			}
		}

		.radio-wrapper {
			margin-left: 1rem;
			display: flex;

			label {
				margin: 0 0.5rem;
			}
		}

		.tooltipped-label-wrapper {
			display: inline-flex;
		}

		.student-choice-select,
		.group-choice-select,
		.class-choice-select {
			display: block;
			margin-top: 0;
		}

		.student-choice,
		.start-date {
			margin-top: 1rem;
		}

		.date-range-wrapper {
			margin: 1rem 0;
			display: grid;
			grid-template-columns: 1fr 1fr;
			gap: 1rem;

			.date-wrapper {
				margin: 0;
			}

			input {
				width: 100%;
			}
		}

		.product-date-list {
			max-height: 20rem;
		}

		.product-date-range {
			margin: 1.5rem 1rem;
			padding-bottom: 1.5rem;
			border-bottom: 1px solid $c-grey;

			&:first-child {
				margin-top: 0.5rem;
			}

			h3 {
				grid-column: 1 / 3;
				margin: 0;
				line-height: 1;
			}
		}

		.product-list {
			h3 {
				margin-bottom: 0.5rem;
			}

			.product-assignment {
				display: flex;
				align-items: center;
				margin-bottom: 0.5rem;
				// padding-top: .5rem;
				border-bottom: 1px solid $c-grey;

				.article-image .article-featured-image-container {
					height: 4rem;
					width: 4rem;
				}

				.product-title {
					padding: 0 0.5rem;
					flex: 1;
					text-overflow: ellipsis;
					white-space: nowrap;
					overflow: hidden;

					.apple-icon,
					.lightning-icon {
						position: relative;
						stroke: $c-blue;
						top: -0.15rem;
					}
				}
			}
		}

		.sub-assignment-summary {
			h4 {
				margin: 1rem auto 0.25rem;
				width: 100%;
				max-width: 18rem;
			}

			&:first-child {
				h4 {
					margin-top: 0;
				}
			}
		}

		.sub-assignment-date-range {
			margin: 0;
			display: grid;
			gap: 0 2rem;
			grid-template-columns: repeat(2, max-content);
			justify-content: center;

			dt {
				font-weight: 700;
			}

			dd {
				margin-left: 0;
			}
		}

		.ell-checkbox {
			svg {
				margin-left: 0.5rem;
			}
		}

		label {
			.apple-icon,
			.lightning-icon {
				stroke: $c-blue;
				margin-left: 0.35rem;
			}
		}
	}

	.scroll-box {
		max-height: 15rem;
		overflow-y: auto;
		border: 1px solid $c-grey;
		margin-bottom: 2rem;
		border-radius: 4px;
		padding: 0.5rem;
		box-shadow: 0px 3px 3px $c-grey;
	}
</style>
<template>
	<modal
		ref="modal"
		id="assignment-wizard"
		:loading="loading"
		:ok="false">
		<template slot="header-container">
			<h2>{{ title || 'Create Assignments' }}</h2>
			<h3 v-if="subtitle">{{ subtitle }}</h3>
			<p
				v-if="categoryText"
				class="margin-none font-small text-muted">
				{{ categoryText }}
			</p>
		</template>
		<template slot="content">
			<div class="modal-content-body">
				<div
					class="assignment-options"
					v-if="step === 0">
					<validated-form :on-submit="nextStep">
						<template v-if="hasAudio">
							<label class="input-wrapper checkbox-wrapper">
								<input
									type="checkbox"
									v-model="audio"
									name="audio" />
								Audio
							</label>
						</template>
						<template v-if="productType === 'AAD'">
							<label class="input-wrapper checkbox-wrapper">
								<input
									type="checkbox"
									v-model="bok"
									name="audio" />
								Book of Knowledge
							</label>
							<label
								class="input-wrapper checkbox-wrapper"
								v-if="boostArticlesPresent">
								<input
									type="checkbox"
									v-model="boost"
									name="boost" />
								Boost Articles
								<apple-icon></apple-icon>
							</label>
							<label
								class="input-wrapper checkbox-wrapper"
								v-if="challengeArticlesPresent">
								<input
									type="checkbox"
									v-model="challenge"
									name="challenge" />
								Challenge Articles
								<lightning-icon></lightning-icon>
							</label>
							<label
								class="input-wrapper checkbox-wrapper"
								v-if="decodablesPresent">
								<input
									type="checkbox"
									v-model="decodables"
									name="decodables" />
								Decodables
							</label>
							<div class="student-choice input-wrapper select-wrapper">
								<div>
									<label
										for="assignment-wizard-student-choice"
										class="margin-none inline-block"
										>Student Choice for Articles</label
									>
									<button
										class="button -link"
										v-tooltip="studentChoiceTip">
										<info-icon></info-icon>
									</button>
								</div>
								<select
									class="student-choice-select"
									id="assignment-wizard-student-choice"
									v-model="studentChoice">
									<option
										v-for="choice in studentChoices"
										:key="choice.value"
										:value="choice.value"
										:selected="studentChoice === choice.value"
										:disabled="choice.disabled">
										{{ choice.text }}
									</option>
								</select>
							</div>
						</template>
						<template v-else>
							<div v-if="hasVocabActivity">
								<label class="input-wrapper checkbox-wrapper tooltipped-label-wrapper">
									<input
										type="checkbox"
										v-model="vocabulary"
										name="vocabulary" />
									Vocabulary Activities
								</label>
								<button
									class="button -link"
									type="button"
									v-tooltip="vocabTooltip">
									<info-icon></info-icon>
								</button>
							</div>
							<template v-if="hasQuestionSets">
								<label class="input-wrapper checkbox-wrapper">
									<input
										type="checkbox"
										v-model="questionSets"
										name="questionSets" />
									Comprehension Questions
								</label>
								<div
									v-if="hasExpressQuestionSets"
									class="input-wrapper radio-wrapper">
									<label>
										<input
											type="radio"
											value="f"
											v-model="fullOrExpress" />
										Full
									</label>
									<label>
										<input
											type="radio"
											value="x"
											v-model="fullOrExpress" />
										Express
									</label>
								</div>
							</template>
						</template>
						<div
							v-if="!flexibleDates"
							class="start-date input-wrapper date-wrapper">
							<label
								for="assignment-start-date"
								class="margin-none"
								>Start Date</label
							>
							<date-picker
								:date.sync="startDate"
								input-id="assignment-start-date"
								:min-date="today"
								:position="'bottom left'">
							</date-picker>
						</div>
						<div class="product-list">
							<h3>{{ productListTitle }}</h3>
							<div class="product-list-box scroll-box">
								<template v-for="(product, idx) in products">
									<div
										v-if="productType === 'AAD'"
										class="product-assignment">
										<article-image
											:image-url="product.img"
											:alt-text="product.imgAlt">
										</article-image>
										<div class="product-title">
											{{ product.title }}
											<apple-icon v-if="product.supplemental_texts && product.supplemental_texts.support"></apple-icon>
											<lightning-icon v-if="product.supplemental_texts && product.supplemental_texts.challenge"></lightning-icon>
										</div>
										<div class="product-start-date">{{ getOffsetDate(idx, startDate) }}</div>
									</div>
									<label
										v-else
										class="input-wrapper checkbox-wrapper">
										<input
											type="checkbox"
											v-model="selectedProducts"
											:value="product.id"
											name="selected-products" />
										{{ product.title }}
									</label>
								</template>
							</div>
						</div>
						<div class="wizard-nav">
							<span class="spacer"></span>
							<button
								type="submit"
								class="next-button button">
								Next
							</button>
						</div>
					</validated-form>
				</div>
				<div
					class="assignment-class"
					v-if="step === 1">
					<validated-form :on-submit="nextStep">
						<div class="class-select input-wrapper select-wrapper">
							<label
								for="assignment-wizard-class-choice"
								class="margin-none"
								>Class</label
							>
							<select
								class="class-choice-select"
								id="assignment-wizard-class-choice"
								v-model="classCode">
								<option
									v-for="cls in classes"
									:key="cls.classCode"
									:value="cls.classCode"
									:selected="classCode === cls.classCode"
									:disabled="cls.disabled">
									{{ cls.name }}
								</option>
							</select>
						</div>
						<div class="student-list">
							<fieldset class="toggle-buttons">
								<label
									class="toggle-button button -small"
									:class="{
										active: assignTo === 's',
										disabled: allStudents.length === 0
									}"
									data-text="Students">
									<input
										:disabled="allStudents.length === 0"
										type="radio"
										name="assign"
										value="s"
										v-model="assignTo" />Students
								</label>
								<label
									class="toggle-button button -small"
									:class="{
										active: assignTo === 'g',
										disabled: allStudents.length === 0 || groups.length === 0
									}"
									data-text="Group">
									<input
										:disabled="allStudents.length === 0 || groups.length === 0"
										type="radio"
										name="assign"
										value="g"
										v-model="assignTo" />
									Group
								</label>
							</fieldset>
							<template v-if="assignTo === 's'">
								<label class="input-wrapper checkbox-wrapper all-students margin-top-small">
									<input
										type="checkbox"
										:checked="allStudents.length === students.length"
										@change="toggleAllStudents" />
									{{ allStudents.length === students.length ? 'Des' : 'S' }}elect All
								</label>
								<div class="scroll-box">
									<label
										v-for="student in allStudents"
										:key="student.uid"
										class="input-wrapper checkbox-wrapper">
										<input
											type="checkbox"
											v-model="students"
											name="students"
											:value="student.uid" />
										{{ student.name }}
									</label>
								</div>
							</template>
							<template v-if="assignTo === 'g'">
								<div class="group-select input-wrapper select-wrapper">
									<select
										class="group-choice-select"
										aria-label="group"
										v-model="group">
										<option
											v-for="g in groups"
											:key="g.value"
											:value="g.value"
											:selected="group === g.value"
											:disabled="g.disabled">
											{{ g.text }}
										</option>
									</select>
								</div>
							</template>
						</div>
						<div
							v-if="productType === 'AAD'"
							class="checkbox-wrapper input-wrapper">
							<label
								class="ell-checkbox margin-none"
								:class="{
									disabled: !currentClassHasEll
								}">
								<input
									class="input-checkbox"
									type="checkbox"
									:disabled="!currentClassHasEll"
									v-model="ell" />
								<span> Turn on ELL and Extra Support features </span>
							</label>
							<button
								class="button -link margin-left-small"
								v-tooltip="ellTip">
								<info-icon aria-label="ELL"></info-icon>
							</button>
						</div>
						<label
							v-if="currentClass && currentClass.gcid"
							class="google-classroom-checkbox input-wrapper checkbox-wrapper">
							<input
								class="input-checkbox"
								type="checkbox"
								v-model="gc" />
							Publish to Google Classroom
						</label>
						<div class="wizard-nav">
							<button
								type="button"
								class="back-button button -outline"
								@click="previousStep">
								Previous
							</button>
							<span class="spacer"></span>
							<button
								type="submit"
								class="next-button button">
								Next
							</button>
						</div>
					</validated-form>
				</div>
				<div
					v-if="flexibleDates && step === 2"
					class="assignment-dates">
					<validated-form :on-submit="nextStep">
						<fieldset
							v-if="selectedProductsSet.size > 1"
							class="input-wrapper">
							<legend>Do you want the same due date for every assignment?</legend>
							<div class="toggle-buttons">
								<label
									class="toggle-button button -small"
									:class="{
										active: useSameDates === 'yes'
									}"
									data-text="Yes">
									<input
										type="radio"
										name="flexibleDates"
										value="yes"
										v-model="useSameDates" />
									Yes
								</label>
								<label
									class="toggle-button button -small"
									:class="{
										active: useSameDates === 'no'
									}"
									data-text="No">
									<input
										type="radio"
										name="flexibleDates"
										value="no"
										v-model="useSameDates" />
									No
								</label>
							</div>
						</fieldset>
						<div
							v-if="useSameDates === 'yes'"
							class="date-range-wrapper">
							<div class="start-date date-wrapper">
								<label for="assignment-start-date">Start Date</label>
								<date-picker
									:date.sync="startDate"
									input-id="assignment-start-date"
									:min-date="today"
									:position="'bottom left'">
								</date-picker>
							</div>
							<div class="end-date date-wrapper">
								<div>
									<label
										for="assignment-end-date"
										class="inline-block"
										>Due Date</label
									>
									<button
										class="button -link"
										v-tooltip="dueDateTooltip">
										<info-icon></info-icon>
									</button>
								</div>
								<date-picker
									:date.sync="endDate"
									input-id="assignment-end-date"
									:min-date="startDate"
									:position="'bottom left'">
								</date-picker>
							</div>
						</div>
						<div
							v-else
							class="product-date-list scroll-box">
							<div
								v-for="product in products"
								:key="product.id"
								v-if="selectedProductsSet.has(product.id)"
								class="product-date-range date-range-wrapper">
								<h3>{{ product.title }}</h3>
								<div class="start-date date-wrapper">
									<label :for="`assignment-start-date-${index}`">Start Date</label>
									<date-picker
										:date.sync="startDates[product.id]"
										:input-id="`assignment-start-date-${index}`"
										:min-date="today"
										:on-date-select="date => onFlexibleStartDateSelect(date, product.id)"
										:position="'bottom left'">
									</date-picker>
								</div>
								<div class="end-date date-wrapper">
									<div>
										<label
											class="inline-block"
											:for="`assignment-end-date-${index}`"
											>Due Date</label
										>
										<button
											class="button -link"
											v-tooltip="dueDateTooltip">
											<info-icon></info-icon>
										</button>
									</div>
									<date-picker
										:date.sync="endDates[product.id]"
										:input-id="`assignment-end-date-${index}`"
										:min-date="startDates[product.id]"
										:position="'bottom left'">
									</date-picker>
								</div>
							</div>
						</div>
						<div class="wizard-nav">
							<button
								type="button"
								class="back-button button -outline"
								@click="previousStep">
								Previous
							</button>
							<span class="spacer"></span>
							<button
								type="submit"
								class="next-button button">
								Next
							</button>
						</div>
					</validated-form>
				</div>
				<div
					class="assignment-confirm"
					v-if="(!flexibleDates && step === 2) || step === 3 || (flexibleDates && step === 4)">
					<validated-form :on-submit="createAssignments">
						<div class="confirm-panel">
							<div class="confirm-group">
								<div class="confirm-item">
									<b>Options:</b> <span>{{ options }}</span>
								</div>
								<template v-for="choice in studentChoices">
									<div
										v-if="productType === 'AAD' && choice.value === studentChoice"
										class="confirm-item">
										<b>Student Choice:</b> {{ choice.text }}
									</div>
								</template>
							</div>
							<div class="confirm-group">
								<div class="confirm-item">
									<b>Assigned To:</b>
									{{ currentClass.name }}
									<template v-if="assignTo === 's'">
										{{ students.length !== currentClass.students.length ? '(' + students.length + ' student' + (students.length > 1 ? 's' : '') + ')' : '' }}
									</template>
									<template v-if="assignTo === 'g'">
										<template v-for="g in groups">
											<span v-if="g.value === group"> - {{ g.text }} </span>
										</template>
									</template>
								</div>
							</div>
						</div>
						<div class="product-list">
							<h3>{{ productListTitle }}</h3>
							<div class="product-list-box scroll-box">
								<template
									v-for="(product, idx) in products"
									v-if="selectedProductsSet.has(product.id)">
									<div
										v-if="productType === 'TS'"
										class="sub-assignment-summary">
										<h4>{{ product.title }}</h4>
										<dl class="sub-assignment-date-range">
											<dt>Start Date:</dt>
											<dd>{{ getFormattedDate(startDates[product.id], 'M/D/YYYY') }}</dd>
											<dt>Due Date:</dt>
											<dd>{{ getFormattedDate(endDates[product.id], 'M/D/YYYY') }}</dd>
										</dl>
									</div>
									<div
										v-else
										class="product-assignment">
										<article-image
											:image-url="product.img"
											:alt-text="product.imgAlt">
										</article-image>
										<div class="product-title">
											{{ product.title }}
											<apple-icon v-if="boost && product.supplemental_texts && product.supplemental_texts.support"></apple-icon>
											<lightning-icon v-if="challenge && product.supplemental_texts && product.supplemental_texts.challenge"></lightning-icon>
										</div>
										<div
											v-if="textSet"
											class="product-start-date">
											{{ getFormattedDate(startDates[product.id]) }}
										</div>
										<div
											v-else
											class="product-start-date">
											{{ getOffsetDate(idx, startDate) }}
										</div>
									</div>
								</template>
							</div>
						</div>
						<div
							class="congratulations text-centered margin-bottom-medium"
							v-if="step === lastStep">
							Your assignments have been created!
						</div>
						<div
							v-if="step !== lastStep && needsGoogleAuth"
							class="margin-top-bottom-medium text-centered">
							<message
								v-if="gcTokenMismatch"
								class="text-left"
								type="error">
								Please authenticate the Google account used to manage this Classroom
							</message>
							<button
								type="button"
								class="google-auth-button button"
								@click="authenticateWithGoogle">
								Authenticate With Google
							</button>
						</div>
						<div class="wizard-nav">
							<button
								v-if="step !== lastStep"
								type="button"
								class="back-button button -outline"
								@click="previousStep">
								Previous
							</button>
							<span class="spacer"></span>
							<button
								v-if="step === lastStep"
								type="button"
								@click="closeModal"
								class="next-button button -outline">
								Close
							</button>
							<button
								v-if="step === lastStep"
								type="button"
								@click="step = 0"
								class="next-button button -outline margin-left-medium">
								Assign Again
							</button>
							<a
								v-if="step === lastStep"
								:href="`/assignments#!classCode:${classCode}`"
								type="button"
								class="next-button button margin-left-medium"
								>Go to Assignments</a
							>
							<span class="spacer"></span>
							<button
								v-if="step !== lastStep"
								type="submit"
								:disabled="needsGoogleAuth"
								class="next-button button"
								:class="{
									disabled: needsGoogleAuth
								}">
								Create
							</button>
						</div>
					</validated-form>
				</div>
			</div>
		</template>
	</modal>
</template>
